import { useEffect, useMemo, useState } from 'react';
import { History, Search, ChevronDown, ChevronUp } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select } from '@/components/ui/Field';
import { EmptyState, Spinner } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { supabase } from '@/lib/supabaseClient';
import { adminApi, type AdminUserRow } from '@/lib/adminApi';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../nav';

type Operation = 'INSERT' | 'UPDATE' | 'DELETE';

interface LogRow {
  id: string;
  actor_id: string | null;
  table_name: string;
  operation: Operation;
  row_id: string | null;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  created_at: string;
}

const TABLE_LABELS: Record<string, string> = {
  core_orders: 'Order',
  core_products: 'Product',
  core_partners: 'Partner',
  core_customer_followups: 'Customer Followup',
  core_followup_attempts: 'Followup Call',
  user_roles: 'Staff Role',
  core_sticker_settings: 'Sticker Settings',
  core_followup_settings: 'Followup Settings',
  app_settings: 'App Settings',
  core_notification_settings: 'Notification Settings',
};

const OPERATION_LABEL: Record<Operation, string> = { INSERT: 'Created', UPDATE: 'Updated', DELETE: 'Deleted' };
const OPERATION_TONE: Record<Operation, 'success' | 'primary' | 'error'> = { INSERT: 'success', UPDATE: 'primary', DELETE: 'error' };

// Noisy on every row regardless of what actually changed — not worth
// surfacing in the diff view.
const SKIP_FIELDS = new Set(['updated_at', 'created_at']);

function changedFields(row: LogRow): { key: string; before: unknown; after: unknown }[] {
  if (row.operation === 'INSERT') {
    return Object.entries(row.new_data ?? {})
      .filter(([k]) => !SKIP_FIELDS.has(k))
      .map(([k, v]) => ({ key: k, before: undefined, after: v }));
  }
  if (row.operation === 'DELETE') {
    return Object.entries(row.old_data ?? {})
      .filter(([k]) => !SKIP_FIELDS.has(k))
      .map(([k, v]) => ({ key: k, before: v, after: undefined }));
  }
  const before = row.old_data ?? {};
  const after = row.new_data ?? {};
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys]
    .filter((k) => !SKIP_FIELDS.has(k))
    .filter((k) => JSON.stringify(before[k]) !== JSON.stringify(after[k]))
    .map((k) => ({ key: k, before: before[k], after: after[k] }));
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

export default function AdminActivityLog() {
  const { push } = useToast();
  const [rows, setRows] = useState<LogRow[]>([]);
  const [users, setUsers] = useState<AdminUserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [tableFilter, setTableFilter] = useState('');
  const [actorFilter, setActorFilter] = useState('');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      supabase.from('activity_log').select('*').order('created_at', { ascending: false }).limit(500),
      adminApi.listUsers().catch(() => ({ users: [] as AdminUserRow[] })),
    ]).then(([logRes, usersRes]) => {
      if (logRes.error) push('error', logRes.error.message);
      setRows((logRes.data as LogRow[]) ?? []);
      setUsers(usersRes.users ?? []);
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const actorName = useMemo(() => {
    const map = new Map<string, string>();
    users.forEach((u) => map.set(u.user_id, u.full_name || u.email));
    return (id: string | null) => (id ? map.get(id) ?? 'Unknown Staff' : 'System (automated)');
  }, [users]);

  const filtered = useMemo(() => {
    let base = rows;
    if (tableFilter) base = base.filter((r) => r.table_name === tableFilter);
    if (actorFilter) base = base.filter((r) => r.actor_id === actorFilter);
    const q = search.trim().toLowerCase();
    if (q) {
      base = base.filter(
        (r) =>
          actorName(r.actor_id).toLowerCase().includes(q) ||
          (r.row_id ?? '').toLowerCase().includes(q) ||
          (TABLE_LABELS[r.table_name] ?? r.table_name).toLowerCase().includes(q),
      );
    }
    return base;
  }, [rows, tableFilter, actorFilter, search, actorName]);

  const tablesPresent = useMemo(() => [...new Set(rows.map((r) => r.table_name))], [rows]);
  const actorsPresent = useMemo(() => [...new Set(rows.map((r) => r.actor_id).filter((id): id is string => !!id))], [rows]);

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div>
          <h1 className="text-[18px] font-bold text-ink">Activity Log</h1>
          <p className="text-[12px] text-ink-faint mt-0.5">Who changed what, and when — orders, products, partners, followups, roles, and settings</p>
        </div>

        <Card>
          <CardHeader title="Filters" icon={<History className="w-4 h-4" />} />
          <CardBody className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Staff">
                <Select value={actorFilter} onChange={(e) => setActorFilter(e.target.value)}>
                  <option value="">All staff</option>
                  {actorsPresent.map((id) => (
                    <option key={id} value={id}>{actorName(id)}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Table">
                <Select value={tableFilter} onChange={(e) => setTableFilter(e.target.value)}>
                  <option value="">All tables</option>
                  {tablesPresent.map((t) => (
                    <option key={t} value={t}>{TABLE_LABELS[t] ?? t}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Search">
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-ink-disabled" />
                  <Input placeholder="Staff, table, or row id" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8" />
                </div>
              </Field>
            </div>
          </CardBody>
        </Card>

        <Card>
          <CardBody className="space-y-2">
            {loading ? (
              <Spinner />
            ) : filtered.length === 0 ? (
              <EmptyState title="Nothing here" hint="No activity matches these filters." />
            ) : (
              filtered.map((r) => {
                const diff = changedFields(r);
                const expanded = expandedId === r.id;
                return (
                  <div key={r.id} className="rounded-xl border border-border-subtle overflow-hidden">
                    <button
                      type="button"
                      onClick={() => setExpandedId(expanded ? null : r.id)}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-surface-muted cursor-pointer text-left"
                    >
                      <Badge tone={OPERATION_TONE[r.operation]}>{OPERATION_LABEL[r.operation]}</Badge>
                      <div className="min-w-0 flex-1">
                        <p className="text-[13px] font-semibold text-ink truncate">
                          {actorName(r.actor_id)} — {TABLE_LABELS[r.table_name] ?? r.table_name}
                        </p>
                        <p className="text-[11px] text-ink-faint truncate">Row: {r.row_id || '—'}</p>
                      </div>
                      <span className="text-[11px] text-ink-faint flex-shrink-0 hidden sm:inline">{new Date(r.created_at).toLocaleString()}</span>
                      {expanded ? <ChevronUp className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" /> : <ChevronDown className="w-3.5 h-3.5 text-ink-faint flex-shrink-0" />}
                    </button>
                    {expanded && (
                      <div className="px-3 pb-3 border-t border-border-subtle pt-2.5">
                        {diff.length === 0 ? (
                          <p className="text-[12px] text-ink-faint">No field-level changes recorded.</p>
                        ) : (
                          <div className="space-y-1.5">
                            {diff.map((d) => (
                              <div key={d.key} className="text-[12px] flex items-start gap-2">
                                <span className="font-mono text-ink-faint flex-shrink-0 w-40 truncate">{d.key}</span>
                                {r.operation === 'UPDATE' ? (
                                  <span className="text-ink-muted">
                                    <span className="line-through text-ink-disabled">{formatValue(d.before)}</span>
                                    {' → '}
                                    <span className="font-semibold text-ink">{formatValue(d.after)}</span>
                                  </span>
                                ) : (
                                  <span className="text-ink-muted">{formatValue(r.operation === 'DELETE' ? d.before : d.after)}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </CardBody>
        </Card>
      </div>
    </AppShell>
  );
}
